/**
 * SportsSpectatorPage — Fullscreen "Big Screen" mode for tournament brackets + live scores.
 * Designed for: TV casting, projector display, phone spectators scanning a QR.
 * Auto-refreshes every 5s. No browser chrome. Minimal UI.
 *
 * URL: /book/:slug/tournament/:tid/spectator
 */
import { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Trophy, Radio, Crown, Maximize, Minimize, Users } from 'lucide-react';
import TournamentBracket, { MatchListView, ColumnsView, groupRounds } from '../components/TournamentBracket';
import { buildLogoMap, groupByDivision, LogoLightbox, useLogoLightbox } from '../components/TeamLogo';
import DivisionPodium, { computePodium } from '../components/WinnersPodium';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RED = '#E63946';

function champOf(format, matches) {
  const fin = matches.filter(m => (m.bracket_type === 'final_reset' || m.bracket_type === 'final') && m.status === 'completed').sort((a, b) => b.round - a.round)[0]
    || (format === 'single_elimination' ? (() => {
        const nonThird = matches.filter(m => m.bracket_type !== 'third_place' && m.bracket_type !== 'losers');
        if (!nonThird.length) return null;
        const maxRound = Math.max(...nonThird.map(m => m.round || 0));
        return nonThird.filter(m => (m.round || 0) === maxRound && m.status === 'completed' && m.winner_side)[0] || null;
      })() : null);
  if (!fin || !fin.winner_side) return null;
  return fin.winner_side === 'a' ? fin.team_a_name : fin.team_b_name;
}

function LiveTicker({ matches }) {
  if (!matches.length) return null;
  return (
    <div data-testid="spectator-live-ticker" style={{ display: 'flex', gap: 12, overflowX: 'auto', padding: '0 0 8px' }}>
      {matches.map(m => (
        <div key={m.id} style={{ flexShrink: 0, background: '#141414', border: `2px solid ${RED}`, borderRadius: 12, padding: '12px 18px', minWidth: 200 }}>
          <div className="flex items-center gap-2 mb-2">
            <Radio className="h-3 w-3 animate-pulse" style={{ color: RED }} />
            <span style={{ fontSize: 10, fontWeight: 800, color: RED, textTransform: 'uppercase', letterSpacing: 1 }}>LIVE · {m.court_name || `Match ${m.match_number}`}</span>
          </div>
          <div className="flex items-center justify-between">
            <span style={{ fontSize: 16, fontWeight: 700, color: '#fff' }}>{m.team_a_name || 'TBD'}</span>
            <span style={{ fontSize: 22, fontWeight: 900, fontFamily: 'Oswald, sans-serif', color: '#fff' }}>{m.score_a}</span>
          </div>
          <div className="flex items-center justify-between mt-1">
            <span style={{ fontSize: 16, fontWeight: 700, color: '#fff' }}>{m.team_b_name || 'TBD'}</span>
            <span style={{ fontSize: 22, fontWeight: 900, fontFamily: 'Oswald, sans-serif', color: '#fff' }}>{m.score_b}</span>
          </div>
          <p style={{ fontSize: 10, color: '#737373', marginTop: 6 }}>Games {m.games_won_a}–{m.games_won_b} · Game {m.current_game}</p>
        </div>
      ))}
    </div>
  );
}

export default function SportsSpectatorPage() {
  const { slug, tid } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const { lightboxUrl, openLightbox, closeLightbox } = useLogoLightbox();

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/sports/public/${slug}/tournaments/${tid}/spectator`);
      setData(r.data);
      setLastUpdate(new Date());
    } catch (e) {
      setError(e.response?.data?.detail || 'Tournament not found');
    }
  }, [slug, tid]);

  useEffect(() => { load(); }, [load]);

  // 5-second auto-refresh
  useEffect(() => {
    const iv = setInterval(load, 5000);
    return () => clearInterval(iv);
  }, [load]);

  // Fullscreen toggle
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().then(() => setIsFullscreen(true)).catch(() => {});
    } else {
      document.exitFullscreen().then(() => setIsFullscreen(false)).catch(() => {});
    }
  };

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  if (error) {
    return (
      <div style={{ minHeight: '100vh', background: '#0A0A0A', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Manrope, sans-serif' }}>
        <div className="text-center">
          <Trophy style={{ width: 48, height: 48, color: '#333', margin: '0 auto 12px' }} />
          <p style={{ fontSize: 18, color: '#737373' }}>{error}</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div style={{ minHeight: '100vh', background: '#0A0A0A', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="animate-spin w-10 h-10 rounded-full" style={{ border: '3px solid #E63946', borderTopColor: 'transparent' }} />
      </div>
    );
  }

  const t = data.tournament;
  const matches = data.matches || [];
  const live = data.live || [];
  const champion = data.champion;
  const logoMap = buildLogoMap(data.teams);
  const progress = data.total_matches > 0 ? Math.round((data.completed_matches / data.total_matches) * 100) : 0;

  // Bracket rendering helpers
  const isDE = t.format === 'double_elimination';
  const isSE = t.format === 'single_elimination';
  const isRR = t.format === 'round_robin';
  const divGroups = groupByDivision(matches, t.divisions);

  return (
    <div data-testid="spectator-mode" style={{ minHeight: '100vh', background: '#0A0A0A', color: '#fff', fontFamily: 'Manrope, sans-serif', cursor: 'default' }}>
      {/* Top bar */}
      <div style={{ background: '#0D0D0D', borderBottom: '2px solid #1A1A1A', padding: '12px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div className="flex items-center gap-3">
          <Trophy style={{ width: 24, height: 24, color: RED }} />
          <div>
            <h1 data-testid="spectator-title" style={{ fontFamily: 'Oswald, sans-serif', fontSize: 22, fontWeight: 900, textTransform: 'uppercase', letterSpacing: 1, color: '#fff', lineHeight: 1 }}>
              {t.name}
            </h1>
            <div className="flex items-center gap-3 mt-0.5">
              <span style={{ fontSize: 11, color: '#737373' }}>{data.facility.name}</span>
              <span style={{ fontSize: 11, color: '#555' }}>
                {t.format?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())} · {t.game_mode?.replace(/_/g, ' ')}
              </span>
              <span style={{ fontSize: 11, color: '#555' }}>
                <Users className="inline h-3 w-3 mr-0.5" />{data.teams?.length || 0} teams
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-4">
          {/* Progress */}
          <div className="hidden sm:flex items-center gap-2">
            <div style={{ width: 120, height: 6, borderRadius: 3, background: '#1A1A1A', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${progress}%`, borderRadius: 3, background: progress >= 100 ? '#22C55E' : RED, transition: 'width 0.5s' }} />
            </div>
            <span style={{ fontSize: 11, color: '#555', fontWeight: 700 }}>{data.completed_matches}/{data.total_matches}</span>
          </div>
          {/* Live indicator */}
          {live.length > 0 && (
            <div className="flex items-center gap-1.5">
              <Radio className="h-3.5 w-3.5 animate-pulse" style={{ color: RED }} />
              <span style={{ fontSize: 11, fontWeight: 800, color: RED }}>{live.length} LIVE</span>
            </div>
          )}
          {/* Last updated */}
          {lastUpdate && (
            <span style={{ fontSize: 10, color: '#333' }}>
              {lastUpdate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </span>
          )}
          {/* Fullscreen toggle */}
          <button data-testid="spectator-fullscreen-btn" onClick={toggleFullscreen}
            style={{ background: '#1A1A1A', border: '1px solid #2A2A2A', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', color: '#A3A3A3', display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 700 }}>
            {isFullscreen ? <Minimize className="h-4 w-4" /> : <Maximize className="h-4 w-4" />}
            {isFullscreen ? 'Exit' : 'Fullscreen'}
          </button>
        </div>
      </div>

      {/* Champion banner — only for single-division (or undivided) tournaments */}
      {champion && (!t.divisions || t.divisions.length <= 1) && (
        <div data-testid="spectator-champion" style={{ background: 'linear-gradient(135deg, #1A0A02, #0D0D0D)', borderBottom: '2px solid #FFD70040', padding: '16px 24px', textAlign: 'center' }}>
          <Crown style={{ width: 28, height: 28, color: '#FFD700', margin: '0 auto 4px' }} />
          <p style={{ fontFamily: 'Oswald, sans-serif', fontSize: 28, fontWeight: 900, color: '#FFD700', textTransform: 'uppercase', letterSpacing: 2 }}>
            {champion}
          </p>
          <p style={{ fontSize: 12, color: '#888', textTransform: 'uppercase', letterSpacing: 1 }}>Champion</p>
        </div>
      )}

      {/* Live ticker */}
      {live.length > 0 && (
        <div style={{ padding: '16px 24px 8px' }}>
          <LiveTicker matches={live} />
        </div>
      )}

      {/* Main bracket area */}
      <div style={{ padding: '16px 24px 40px', overflowX: 'auto' }}>
        {/* Champions podium(s) — one per decided division */}
        {(() => {
          const podiums = divGroups
            .map(g => ({ divName: g.divName, podium: computePodium(t.format, g.matches, data.standings?.[g.divId]) }))
            .filter(p => p.podium);
          if (!podiums.length) return null;
          return (
            <div style={{ display: 'grid', gap: 16, marginBottom: 28,
              gridTemplateColumns: podiums.length > 1 ? 'repeat(auto-fit, minmax(320px, 1fr))' : '1fr' }}>
              {podiums.map(p => <DivisionPodium key={p.divName || 'all'} divName={p.divName} podium={p.podium} logoMap={logoMap} onLogo={openLightbox} teams={data.teams || []}
                cardMeta={{ eventName: t.name, facilityName: data.facility?.name, date: t.date_start }} />)}
            </div>
          );
        })()}
        {/* Single / Double Elimination — one bracket per division */}
        {(isSE || isDE) && divGroups.map(g => {
          const gChamp = champOf(t.format, g.matches);
          const wb = g.matches.filter(m => m.bracket_type === 'winners');
          const lb = g.matches.filter(m => m.bracket_type === 'losers');
          const finals = g.matches.filter(m => m.bracket_type === 'final' || (m.bracket_type === 'final_reset' && m.status !== 'cancelled'));
          const bronze = g.matches.find(m => m.bracket_type === 'third_place');
          const seMatches = isSE ? g.matches.filter(m => m.bracket_type !== 'third_place') : [];
          return (
            <div key={g.divId || 'all'} data-testid={`spectator-division-${g.divId || 'all'}`} style={{ marginBottom: 32 }}>
              {g.divName && (
                <p style={{ fontFamily: 'Oswald, sans-serif', fontSize: 18, fontWeight: 900, color: RED, textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 12 }}>{g.divName}</p>
              )}
              {isSE && seMatches.length > 0 && (
                <div>
                  <TournamentBracket rounds={groupRounds(seMatches)} showChampion={!!gChamp} champion={gChamp} logoMap={logoMap} onLogo={openLightbox} />
                  {bronze && (
                    <div style={{ marginTop: 20 }}>
                      <p style={{ fontSize: 11, fontWeight: 800, color: '#CD7F32', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>3rd Place Match</p>
                      <div style={{ maxWidth: 300, background: '#0F0F0F', border: '1px solid #2A2A2A', borderRadius: 12, padding: '12px 16px' }}>
                        <div className="flex items-center justify-between">
                          <span style={{ fontSize: 14, fontWeight: 700, color: bronze.winner_side === 'a' ? '#22C55E' : '#fff' }}>{bronze.team_a_name || 'TBD'}</span>
                          <span style={{ fontSize: 16, fontWeight: 900, fontFamily: 'Oswald, sans-serif', color: '#fff' }}>{bronze.status === 'completed' ? bronze.games_won_a : bronze.score_a}</span>
                        </div>
                        <div className="flex items-center justify-between mt-1">
                          <span style={{ fontSize: 14, fontWeight: 700, color: bronze.winner_side === 'b' ? '#22C55E' : '#fff' }}>{bronze.team_b_name || 'TBD'}</span>
                          <span style={{ fontSize: 16, fontWeight: 900, fontFamily: 'Oswald, sans-serif', color: '#fff' }}>{bronze.status === 'completed' ? bronze.games_won_b : bronze.score_b}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
              {isDE && (
                <div className="space-y-8">
                  {wb.length > 0 && (
                    <div>
                      <p style={{ fontSize: 12, fontWeight: 800, color: '#34D399', textTransform: 'uppercase', letterSpacing: 2, marginBottom: 8 }}>Winners Bracket</p>
                      <TournamentBracket rounds={groupRounds(wb)} showChampion={false} logoMap={logoMap} onLogo={openLightbox} />
                    </div>
                  )}
                  {lb.length > 0 && (
                    <div>
                      <p style={{ fontSize: 12, fontWeight: 800, color: '#F59E0B', textTransform: 'uppercase', letterSpacing: 2, marginBottom: 8 }}>Losers Bracket</p>
                      <ColumnsView matches={lb} logoMap={logoMap} onLogo={openLightbox} />
                    </div>
                  )}
                  {finals.length > 0 && (
                    <div>
                      <p style={{ fontSize: 12, fontWeight: 800, color: RED, textTransform: 'uppercase', letterSpacing: 2, marginBottom: 8 }}>
                        Grand Final{gChamp ? ` · Champion: ${gChamp}` : ''}
                      </p>
                      <div className="flex gap-3 flex-wrap" style={{ maxWidth: 500 }}>
                        {finals.map(m => (
                          <div key={m.id} style={{ flex: '1 1 220px', background: '#0F0F0F', border: `1px solid ${m.status === 'live' ? RED : '#2A2A2A'}`, borderRadius: 12, padding: '12px 16px' }}>
                            <div className="flex items-center justify-between">
                              <span style={{ fontSize: 14, fontWeight: 700, color: m.winner_side === 'a' ? '#22C55E' : '#fff' }}>
                                {m.winner_side === 'a' && <Crown className="inline h-3 w-3 mr-1" style={{ color: '#22C55E' }} />}{m.team_a_name || 'TBD'}
                              </span>
                              <span style={{ fontSize: 16, fontWeight: 900, fontFamily: 'Oswald, sans-serif' }}>{m.status === 'completed' ? m.games_won_a : m.score_a}</span>
                            </div>
                            <div className="flex items-center justify-between mt-1">
                              <span style={{ fontSize: 14, fontWeight: 700, color: m.winner_side === 'b' ? '#22C55E' : '#fff' }}>
                                {m.winner_side === 'b' && <Crown className="inline h-3 w-3 mr-1" style={{ color: '#22C55E' }} />}{m.team_b_name || 'TBD'}
                              </span>
                              <span style={{ fontSize: 16, fontWeight: 900, fontFamily: 'Oswald, sans-serif' }}>{m.status === 'completed' ? m.games_won_b : m.score_b}</span>
                            </div>
                            {m.status === 'live' && <p style={{ fontSize: 10, color: RED, marginTop: 4 }}>● LIVE</p>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {/* Round Robin */}
        {isRR && (
          <div className="space-y-6">
            {Object.keys(data.standings || {}).length > 0 && (
              <div>
                <p style={{ fontSize: 12, fontWeight: 800, color: '#fff', textTransform: 'uppercase', letterSpacing: 2, marginBottom: 8 }}>Standings</p>
                {Object.entries(data.standings).map(([divId, rows]) => (
                  <div key={divId} className="mb-4">
                    {divId !== '__all__' && <p style={{ fontSize: 11, color: '#737373', marginBottom: 4 }}>{t.divisions?.find(d => d.id === divId)?.name || divId}</p>}
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 440 }}>
                        <thead>
                          <tr style={{ borderBottom: '1px solid #2A2A2A' }}>
                            <th style={{ textAlign: 'left', padding: '8px', color: '#555', fontSize: 10, textTransform: 'uppercase' }}>#</th>
                            <th style={{ textAlign: 'left', padding: '8px', color: '#555', fontSize: 10, textTransform: 'uppercase' }}>Team</th>
                            <th style={{ textAlign: 'center', padding: '8px', color: '#555', fontSize: 10 }}>Wins</th>
                            <th style={{ textAlign: 'center', padding: '8px', color: '#555', fontSize: 10 }}>Losses</th>
                            <th style={{ textAlign: 'center', padding: '8px', color: '#555', fontSize: 10 }}>Pts For</th>
                            <th style={{ textAlign: 'center', padding: '8px', color: '#555', fontSize: 10 }}>Pts Against</th>
                            <th style={{ textAlign: 'center', padding: '8px', color: '#555', fontSize: 10 }}>Pt. Diff</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((row, i) => (
                            <tr key={row.team_id} style={{ borderBottom: '1px solid #1A1A1A', background: i === 0 ? '#22C55E08' : 'transparent' }}>
                              <td style={{ padding: '8px', color: i === 0 ? '#22C55E' : '#737373', fontWeight: 700 }}>{i + 1}</td>
                              <td style={{ padding: '8px', color: '#fff', fontWeight: 600 }}>{row.team_name}</td>
                              <td style={{ textAlign: 'center', padding: '8px', color: '#22C55E', fontWeight: 700 }}>{row.wins}</td>
                              <td style={{ textAlign: 'center', padding: '8px', color: '#EF4444' }}>{row.losses}</td>
                              <td style={{ textAlign: 'center', padding: '8px', color: '#A3A3A3' }}>{row.points_for}</td>
                              <td style={{ textAlign: 'center', padding: '8px', color: '#A3A3A3' }}>{row.points_against}</td>
                              <td style={{ textAlign: 'center', padding: '8px', color: row.diff >= 0 ? '#22C55E' : '#EF4444' }}>{row.diff > 0 ? '+' : ''}{row.diff}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div>
              <p style={{ fontSize: 12, fontWeight: 800, color: '#fff', textTransform: 'uppercase', letterSpacing: 2, marginBottom: 8 }}>Matches</p>
              <MatchListView matches={matches} logoMap={logoMap} onLogo={openLightbox} />
            </div>
          </div>
        )}

        {/* No matches yet */}
        {matches.length === 0 && (
          <div style={{ textAlign: 'center', padding: '80px 20px' }}>
            <Trophy style={{ width: 48, height: 48, color: '#1A1A1A', margin: '0 auto 12px' }} />
            <p style={{ fontSize: 18, color: '#333', fontFamily: 'Oswald, sans-serif', textTransform: 'uppercase' }}>Bracket not generated yet</p>
            <p style={{ fontSize: 13, color: '#555', marginTop: 4 }}>{data.teams?.length || 0} teams registered</p>
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, background: '#0A0A0AEE', borderTop: '1px solid #1A1A1A', padding: '8px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 10, color: '#333' }}>Powered by AgriBooks Sports</span>
        <span style={{ fontSize: 10, color: '#333' }}>Auto-refreshing every 5s</span>
      </div>
      <LogoLightbox url={lightboxUrl} onClose={closeLightbox} />
    </div>
  );
}
